import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Table, Header, Divider } from 'semantic-ui-react';
import { usePopup } from '../../lib/popup';

import UserAddStepContainer from '../../containers/UserAddStepContainer';
import Item from './Item';

const UsersModal = React.memo(
  ({
    items,
    canAdd,
    onUpdate,
    onUsernameUpdate,
    onUsernameUpdateMessageDismiss,
    onEmailUpdate,
    onEmailUpdateMessageDismiss,
    onPasswordUpdate,
    onPasswordUpdateMessageDismiss,
    onDelete,
    onClose,
    isEmbedded = false,
  }) => {
    const [t] = useTranslation();

    const handleUpdate = useCallback(
      (id, data) => {
        onUpdate(id, data);
      },
      [onUpdate],
    );

    const handleUsernameUpdate = useCallback(
      (id, data) => {
        onUsernameUpdate(id, data);
      },
      [onUsernameUpdate],
    );

    const handleUsernameUpdateMessageDismiss = useCallback(
      (id) => {
        onUsernameUpdateMessageDismiss(id);
      },
      [onUsernameUpdateMessageDismiss],
    );

    const handleEmailUpdate = useCallback(
      (id, data) => {
        onEmailUpdate(id, data);
      },
      [onEmailUpdate],
    );

    const handleEmailUpdateMessageDismiss = useCallback(
      (id) => {
        onEmailUpdateMessageDismiss(id);
      },
      [onEmailUpdateMessageDismiss],
    );

    const handlePasswordUpdate = useCallback(
      (id, data) => {
        onPasswordUpdate(id, data);
      },
      [onPasswordUpdate],
    );

    const handlePasswordUpdateMessageDismiss = useCallback(
      (id) => {
        onPasswordUpdateMessageDismiss(id);
      },
      [onPasswordUpdateMessageDismiss],
    );

    const handleDelete = useCallback(
      (id) => {
        onDelete(id);
      },
      [onDelete],
    );

    const UserAddPopupContainer = usePopup(UserAddStepContainer);

    // Organizar usuarios por tipo
    const { globalAdmins, organizationUsers, usersWithoutOrg } = useMemo(() => {
      const globalAdmins = items.filter(item => item.isAdmin);
      const organizationUsers = items.filter(item => !item.isAdmin && item.organization);
      const usersWithoutOrg = items.filter(item => !item.isAdmin && !item.organization);

      return { globalAdmins, organizationUsers, usersWithoutOrg };
    }, [items]);

    const renderUserSection = (users, title, description) => {
      if (users.length === 0) return null;
      
      return (
        <div style={{ marginBottom: title ? '2rem' : '1rem' }}>
          {title && (
            <Header as="h4" style={{ margin: '1rem 0 0.5rem 0', color: '#2185d0' }}>
              {title}
              {description && (
                <Header.Subheader style={{ color: '#666', fontWeight: 'normal' }}>
                  {description}
                </Header.Subheader>
              )}
            </Header>
          )}
          <Table unstackable basic="very" style={{ marginTop: '0.5rem' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell />
                <Table.HeaderCell width={6}>{t('common.name')}</Table.HeaderCell>
                <Table.HeaderCell width={6}>{t('common.email')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{t('common.orgAdmin')}</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.map((item) => (
                <Item
                  key={item.id}
                  email={item.email}
                  username={item.username}
                  name={item.name}
                  avatarUrl={item.avatarUrl}
                  organization={item.organization}
                  phone={item.phone}
                  isAdmin={item.isAdmin}
                  organizationRole={item.organizationRole}
                  isGlobalAdmin={item.isGlobalAdmin}
                  isOrganizationAdmin={item.isOrganizationAdmin}
                  canManageUsers={item.canManageUsers}
                  isLocked={item.isLocked}
                  isRoleLocked={item.isRoleLocked}
                  isUsernameLocked={item.isUsernameLocked}
                  isDeletionLocked={item.isDeletionLocked}
                  emailUpdateForm={item.emailUpdateForm}
                  passwordUpdateForm={item.passwordUpdateForm}
                  usernameUpdateForm={item.usernameUpdateForm}
                  onUpdate={(data) => handleUpdate(item.id, data)}
                  onUsernameUpdate={(data) => handleUsernameUpdate(item.id, data)}
                  onUsernameUpdateMessageDismiss={() => handleUsernameUpdateMessageDismiss(item.id)}
                  onEmailUpdate={(data) => handleEmailUpdate(item.id, data)}
                  onEmailUpdateMessageDismiss={() => handleEmailUpdateMessageDismiss(item.id)}
                  onPasswordUpdate={(data) => handlePasswordUpdate(item.id, data)}
                  onPasswordUpdateMessageDismiss={() => handlePasswordUpdateMessageDismiss(item.id)}
                  onDelete={() => handleDelete(item.id)}
                />
              ))}
            </Table.Body>
          </Table>
        </div>
      );
    };

    const content = (
      <div>
        {!isEmbedded && (
          <Modal.Header>
            {t('common.users', {
              context: 'title',
            })}
          </Modal.Header>
        )}
        <div style={{ padding: isEmbedded ? 0 : '1rem' }}>
          <div style={{ padding: '0.5rem 0' }}>
            {renderUserSection(globalAdmins, null, null)}
            {renderUserSection(organizationUsers, null, null)}
            {renderUserSection(usersWithoutOrg, null, null)}
          </div>
        </div>
        {canAdd && !isEmbedded && (
          <div style={{ padding: '1rem', borderTop: '1px solid #ddd' }}>
            <UserAddPopupContainer>
              <Button positive content={t('action.addUser')} />
            </UserAddPopupContainer>
          </div>
        )}
      </div>
    );

    return isEmbedded ? content : (
      <Modal open closeIcon size="large" centered={false} onClose={onClose}>
        {content}
      </Modal>
    );
  },
);

UsersModal.propTypes = {
  items: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  canAdd: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onUsernameUpdate: PropTypes.func.isRequired,
  onUsernameUpdateMessageDismiss: PropTypes.func.isRequired,
  onEmailUpdate: PropTypes.func.isRequired,
  onEmailUpdateMessageDismiss: PropTypes.func.isRequired,
  onPasswordUpdate: PropTypes.func.isRequired,
  onPasswordUpdateMessageDismiss: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isEmbedded: PropTypes.bool,
};

export default UsersModal;
